import _ from 'lodash';
import { subMonths, startOfToday, endOfToday, formatDistanceStrict } from 'date-fns';
import { secondsToDate, minutesToTime } from 'Utils/filters';
import Modal from './dynamicEvents.modal.component';
import template from './dynamicEvents.html';


export default {
  name: 'dynamicEvents',
  template,
  controller: DynamicEventsController,
  controllerAs: 'DynEventsCtrl',
  bindings: {
    options: '<',
    queryParams: '<',
    defaultTableOptions: '<'
  }
};

/* @ngInject */
function DynamicEventsController(
  $log,
  $filter,
  growl,
  EventInfoService,
  ReportsService,
  ModalService,
  SiteService,
  uiGridConstants,
) {
  const vm = this;

  let infiniteScrollDeregister = _.noop;
  let filterChangedDeregister = _.noop;
  let sortChangedDeregister = _.noop;
  let beginEditDeregister = _.noop;
  let afterEditDeregister = _.noop;
  const LIMIT = 25;
  const FILTER_DEBOUNCE = 500;
  const SORT_DEBOUNCE = 250;
  const DEFAULT_SORT = ['eventDate', -1];
  const OPERATORS = ['>=', '<=', '!=', '>', '<', '~', '=', '^'];
  let sort = [DEFAULT_SORT];
  let filter;
  let moreEventsToLoad = false;
  let metadata = {};
  let eventBackups = {};

  vm.data = [];

  vm.gridOptions = _.defaults(getReportsConfig('waterDisposal'), {
    data: vm.data,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    appScopeProvider: vm,
    infiniteScrollDown: true,
    infiniteScrollRowsFromEnd: 40,
    useExternalFiltering: true,
    useExternalSorting: true,
    gridFooterTemplate: [
      '<div class="ui-grid-footer-info ui-grid-grid-footer">',
      '<span>Loaded {{grid.appScope.data.length || 0}} of {{grid.appScope.totalItems}} items.</span>',
      '</div>'
    ].join(''),
    onRegisterApi: (gridApi) => {
      vm.gridApi = gridApi;
      infiniteScrollDeregister = gridApi.infiniteScroll.on.needLoadMoreData(null, infiniteScrollDownHandler, vm);
      filterChangedDeregister = gridApi.core.on.filterChanged(
        null,
        _.debounce(filterChangedHandler, FILTER_DEBOUNCE),
        vm
      );
      sortChangedDeregister = gridApi.core.on.sortChanged(
        null,
        _.debounce(sortChangedHandler, SORT_DEBOUNCE),
        vm
      );
      beginEditDeregister = gridApi.edit.on.beginCellEdit(null, (rowEntity) => {
        // only clone backup if this is the first edit, since it's 'clean'
        if (!eventBackups[rowEntity.id]) {
          eventBackups[rowEntity.id] = _.cloneDeep(rowEntity);
        }
      }, vm);
      afterEditDeregister = gridApi.edit.on.afterCellEdit(null, (rowEntity) => {
        const backup = _.get(eventBackups, rowEntity.id);
        gridApi.grid.getRow(rowEntity).isDirty = !_.isEqual(backup, rowEntity);
      }, vm);
    }
  });

  vm.title = vm.gridOptions.title;

  // if false, controller will load event types / dashboards but will not load data for report
  // if true, will load data
  const _loadDataOnInitialize = false;

  vm.listOfSites = [];
  vm.eventNamesConfig = [];
  vm.playStates = {};
  vm.selectedSite = '';
  vm.selectedEventNames = [];
  vm.exportEventsCount = 0;
  vm.disableExport = true;
  vm.totalItems = 0;

  vm.$onInit = () => {

    // vm.user = appConfig.user;
    vm.options = angular.copy(vm.options);
    const start = subMonths(startOfToday(), 1);
    const end = endOfToday();
    _.merge(vm.options, {
      date: {
        end,
        start
      }
    });

    vm.queryParams = angular.copy(vm.queryParams);
    loadSites();

    if (_loadDataOnInitialize) {
      // createDataTable();
    }
  };

  vm.generateEventsReport = generateEventsReport;
  vm.createP18Report = createP18Report;
  vm.getData = getData;
  vm.printTransaction = printTransaction;
  vm.onChangedSite = onChangedSite;

  vm.openEventDetailsModalSettings = ModalService.openEventDetailsModalSettings;
  vm.openCopyReportLink = ModalService.openCopyReportLink;

  vm.setRowState = async (eventObj, newState) => {
    const { id, meta = {} } = eventObj;
    try {
      const update = {
        meta: {
          ...meta,
          // Clear state if already chosen
          state: meta.state === newState ? '' : newState,
        }
      };
      const { data: [event] } = await EventInfoService.updateEvent(id, update);
      _.merge(eventObj, event);
      vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.EDIT);
    } catch (err) {
      console.error(err);
      growl.error(err.error, { title: 'Could not update transaction state' });
    }
  };

  vm.updateRow = async (eventObj) => {
    try {
      let event = {};
      const update = _.cloneDeep(eventObj);
      [event] = await EventInfoService.updateEvent(eventObj.id, update)
        .then(({ data }) => data);
      _.merge(eventObj, event);
      // reset isDirty and backup states
      vm.gridApi.grid.getRow(eventObj).isDirty = false;
      delete eventBackups[eventObj.id];
      vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.EDIT);
    } catch (err) {
      growl.error(err.error, { title: 'Could not update the event' });
    }
  };

  vm.cancelEdits = (eventObj) => {
    // revert back to pre-stored data before edits
    _.merge(eventObj, eventBackups[eventObj.id]);
    vm.gridApi.grid.getRow(eventObj).isDirty = false;
  };

  async function infiniteScrollDownHandler() {
    const events = await fetchAdditionalEvents();
    vm.gridApi.infiniteScroll.saveScrollPercentage();
    vm.data.push(...events);
    calculateTimeDifference(vm.data);
    return vm.gridApi.infiniteScroll.dataLoaded(false, moreEventsToLoad);
  }

  function filterChangedHandler() {
    const columns = vm.gridApi.grid.getOnlyDataColumns();
    const newFilter = _.reduce(columns, (acc, col) => {
      _.forEach(col.filters || [], (fil) => {
        if (fil.term) {
          // Check that the column is exclusively numbers, if so search mongo for exact number. no partials
          let operator = col.colDef.type === 'number' ? '=' : '^'; // default bc it uses indexes if present
          let term = fil.term;
          _.forEach(OPERATORS, (op) => {
            let ret = true;
            if (_.startsWith(term, op)) {
              operator = op;
              term = term.replace(op, '');
              ret = false;
            }
            return ret;
          });
          if (term.length) {
            acc.push(`${col.field}${operator}${term}`);
          }
        }
      });
      return acc;
    }, []).join('|');

    if (newFilter !== filter) {
      filter = newFilter;
      generateEventsReport();
    }
  }

  function sortChangedHandler(_grid, sortColumns) {
    sort = sortColumns.length ?
      _.map(sortColumns, col => [col.field, col.sort.direction === 'asc' ? 1 : -1]) :
      [DEFAULT_SORT];

    generateEventsReport();
  }

  async function fetchAdditionalEvents(limit = LIMIT) {
    let events = [];
    if (moreEventsToLoad) {
      ({ metadata, events } = await getData(limit, vm.data.length));
      const total = metadata.total || 0;
      moreEventsToLoad = vm.data.length + events.length < total;
      vm.totalItems = total;
    }
    return events;
  }

  async function getData(limit = LIMIT, skip = 0) {
    const data = { metadata: {}, events: [] };
    try {
      // get events
      _.merge(
        data,
        await EventInfoService.getEventsGrouped({
          equipment: vm.selectedSite.equipment,
          startDate: vm.options.date.start.toISOString(),
          endDate: vm.options.date.end.toISOString(),
          eventNames: ['toptech/transaction', 'ticketingSwd/transaction'],
          limit,
          skip,
          sort,
          extraCriteria: { sum: 'data.Volume', filter }
        })
      );

    } catch (err) {
      const msg = err.error || err.data || err.message;
      growl.error(msg);
      $log.error(err);
    } finally {
      vm.isLoading = false;
    }
    return data;
  }

  async function generateEventsReport() {
    moreEventsToLoad = true;
    if (!_.isObject(vm.selectedSite)) {
      const msg = 'No Site selected. Please select a site and try again';
      growl.error(msg);
      return;
    }

    eventBackups = {};
    vm.data.length = 0;
    vm.data.push(...await fetchAdditionalEvents(100));
    calculateTimeDifference(vm.data);
    generateReportName();
    // 'fresh' report generation, reset the scroll and let it know only more data downwards if available
    // resetScroll(moreDataAvailUp, moreDataAvailDown)
    _.get(vm, ['gridApi', 'infiniteScroll', 'resetScroll'], _.noop)(false, moreEventsToLoad);
    vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.ALL);
  }

  async function loadRemainingDataForExport() {
    vm.data.push(...await fetchAdditionalEvents(vm.totalItems));
    calculateTimeDifference(vm.data);
    return vm.data;
  }

  async function loadSites() {
    // TODO: API should filter these for us
    const validSites = _.filter(await SiteService.get(), site => site.info?.disposalWellName);
    vm.listOfSites = _.chain(validSites)
      .map(fullSite => _.pick(fullSite, ['id', 'name', 'equipment', 'info']))
      .sortBy('name')
      .value();

    if (vm.queryParams.site) {
      const { site } = vm.queryParams;
      vm.selectedSite = _.find(vm.listOfSites, { id: site });
      if (vm.selectedSite) {
        onChangedSite();
      } else {
        growl.error(`Site ${_.isString(site) ? site : ''} not found`);
      }
    }
    safeDeleteQueryParams();
  }

  async function printTransaction(eventId) {
    try {
      const { body: { data = {} } } = await EventInfoService.getEvent(eventId);
      const STARS = 11;
      const printTicketWindow = window.open('', `Print Transaction ${eventId}`, 'height=800,width=1000');
      const imagesHTML =
          _.reduce(_.get(data, 'media.images', []), (acc, img) => {
            acc += `<div>
                      <img src="${_.get(img, 'sslUrl', '')}" />
                    </div>
                    <br />`;
            return acc;
          }, '');
      const ticketHTML =
          `<html><head><title>Print SWD Ticket ${_.get(data, 'DisposalTicketNo', '')}</title>
          <style>img{width:100%};</style>
          <script>
            window.onload = (event) => {
              window.print();
            };
          </script>
          </head><body>
          <div>${_.repeat('*', STARS)} SWD Ticket ${_.repeat('*', STARS)}</div>
          <br />
          <div>${_.get(data, 'SWDSite.SWDSiteOperatorName', '')}</div>
          <div>Facility : ${_.get(data, 'SWDSite.SWDSiteName', '')}</div>
          <div>Lane : ${_.get(data, 'SWDSiteLane.LaneName', '')}</div>
          <br />
          <div>Ticket # : ${_.get(data, 'DisposalTicketNo', '')}</div>
          <div>Start Date : ${_.get(data, 'StartDate', '')}</div>
          <div>Start Time : ${_.get(data, 'StartTime', '')}</div>
          <div>End Time : ${_.get(data, 'EndTime', '')}</div>
          <br />
          <div>Service Company : ${_.get(data, 'HaulerCo.HaulerCoName', '')}</div>
          <div>Driver : ${_.get(data, 'TruckDriverName', '')}</div>
          <div>Truck # : ${_.get(data, 'TruckNumber', '')}</div>
          <div>Service Ticket # : ${_.get(data, 'ServiceTicketNo', '')}</div>
          <br />
          <div>Source Operator : ${_.get(data, 'WellLease.Operator.OperatorName', '')}</div>
          <div>Source Name : ${_.get(data, 'WellLease.LeaseName', '')}</div>
          <div>Source # : ${_.get(data, 'WellLease.LeaseNumber', '')}</div>
          <div>Well # : ${_.get(data, 'WellLease.WellNumber', '')}</div>
          <br />
          <div>Material : ${_.get(data, 'MaterialName', '')}</div>
          <div>Volume : ${_.get(data, 'Volume', '')}</div>
          <br />
          <div>Printed : ${new Date(data.StopEpochTime * 1000)}</div>
          <br />
          <div>${_.repeat('*', STARS)} SWD Ticket ${_.repeat('*', STARS)}</div>
          <br />
          ${imagesHTML}
          </body></html>`;
        // create printable view

      printTicketWindow.document.write(ticketHTML);
      printTicketWindow.document.close();

    } catch {
      growl.error('Unable to generate ticket');
    }
  }

  function onChangedSite() {
    vm.disableExport = true;
    generateReportName();
  }

  function safeDeleteQueryParams() {
    if (
      !vm.listOfSites ||
        !vm.listOfSites.length ||
        !vm.eventNamesConfig ||
        !vm.eventNamesConfig.length
    ) {
      return;
    }
    if (
      vm.queryParams.start ||
        vm.queryParams.end ||
        vm.queryParams.dashboard ||
        vm.queryParams.eventTypes
    ) {
      vm.isLoading = false;
      generateEventsReport();
    }
    delete vm.queryParams;
  }

  async function createP18Report() {
    if (!_.isObject(vm.selectedSite)) {
      growl.error('No Site selected. Please select a site and try again');
      return false;
    }

    return openModal()
      .then(downloadReport)
      .catch(_.noop); // User closed modal

  }

  /**
   * @param {object} options P18 Data
   * @returns {Promise<undefined>}
   */
  async function downloadReport(options) {
    try {
      const { data } = await ReportsService.createPdf({ pdfType: 'p18', options });

      const blob = new Blob([data], {
        type: 'application/pdf'
      });
      growl.success('P18 Created');
      return saveAs(blob, `P18Report${Date.now()}.pdf`);
    } catch {
      return growl.error('Pdf Creation Failed');
    }
  }

  function openModal() {
    return ModalService.open({
      ...Modal,
      size: 'lg',
      resolve: {
        site: vm.selectedSite
      }
    });
  }

  vm.$onDestroy = () => {
    infiniteScrollDeregister();
    filterChangedDeregister();
    sortChangedDeregister();
    beginEditDeregister();
    afterEditDeregister();
  };

  function toColObj(properties) {
    return _.merge(
      {},
      {
        cellTooltip: true,
        headerTooltip: true,
        width: 225,
        type: 'string',
        headerCellClass: 'text-center'
      },
      properties
    );
  }

  function getReportsConfig(reportType = 'waterDisposal') {
    vm.getRowStyle = ({ meta: { state } = {} }) => state === 'approved' ?
      'text-success bg-success' :
      state === 'void' ?
        'text-muted' :
        'text-danger bg-danger';
    return {
      waterDisposal: {
        title: 'Water Disposal',
        enableCellEdit: true,
        enableFiltering: true,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableGridMenu: true,
        hidePinRight: true,
        showColumnFooter: true,
        showGridFooter: true,
        exporterMenuExcel: false,
        exporterMenuPdf: false,
        exporterFieldCallback: waterDisposalExportFieldCallback,
        exporterCsvFilename: 'Trucks.csv',
        exporterAllDataFn: loadRemainingDataForExport,
        columnDefs: [
          toColObj({
            name: 'actions',
            displayName: 'Actions',
            cellClass: 'text-center',
            enableSorting: false,
            enableColumnResizing: false,
            enableFiltering: false,
            enableCellEdit: false,
            enableHiding: false,
            hidePinRight: true,
            hidePinLeft: true,
            pinnedLeft: true,
            enableColumnMenu: false,
            enableColumnMoving: false,
            exporterSuppressExport: true,
            width: 50 * 3,
            cellTemplate:
                `<div class="grid-action-cell">
                  <span ng-if="!row.isDirty">
                    <button class="btn btn-sm"
                      ng-class="row.entity.meta.state === 'approved' ? 'btn-warning' : 'btn-success'"
                      data-toggle="tooltip"
                      title="{{row.entity.meta.state === 'approved' ? 'Revert Approval' : 'Approve Transaction'}}"
                      ng-click="grid.appScope.setRowState(row.entity, 'approved');"
                    >
                      <i
                        class="far fa-sm flat"
                        ng-class="row.entity.meta.state === 'approved' ? 'fa-undo' : 'fa-check'"
                      ></i>
                    </button>
                    <button class="btn btn-sm"
                      ng-class="row.entity.meta.state === 'void' ? 'btn-warning' : 'btn-danger'"
                      data-toggle="tooltip"
                      title="{{row.entity.meta.state === 'void' ? 'Undo Void' : 'Void Transaction'}}"
                      ng-click="grid.appScope.setRowState(row.entity, 'void');"
                    >
                      <i
                        class="far fa-sm flat"
                        ng-class="row.entity.meta.state === 'void' ? 'fa-undo' : 'fa-times'"
                      ></i>
                    </button>
                  </span>
                  <span ng-if="row.isDirty">
                    <button class="btn btn-default btn-sm"
                      data-toggle="tooltip" title="Cancel changes"
                      ng-click="grid.appScope.cancelEdits(row.entity);"
                      ><i class="far fa-sm fa-times flat"></i></button>
                    <button class="btn btn-primary btn-sm"
                      data-toggle="tooltip" title="Save changes"
                      ng-click="grid.appScope.updateRow(row.entity);"
                      ><i class="far fa-sm fa-save"></i></button>
                  </span>
                  <button class="btn btn-default btn-sm"
                    data-toggle="tooltip" title="Print Transaction"
                    ng-click="grid.appScope.printTransaction(row.entity.id);"
                    ><i class="far fa-sm fa-print"></i></button>
                </div>`,
          }), {
            name: 'meta.state',
            displayName: 'State',
            cellTooltip: true,
            enableCellEdit: false,
            pinnedLeft: true,
            width: 105,
            cellTemplate: `<div
                class='ui-grid-cell-contents'
                data-toggle="tooltip"
                title="{{row.entity.meta.state || 'pending'}}"
                ng-class="grid.appScope.getRowStyle(row.entity)">{{row.entity.meta.state || 'pending'}}</div>`,
            filter: {
              selectOptions: [
                { value: 'approved', label: 'Approved' },
                { value: 'void', label: 'Voided' },
                { value: '!=approved|meta.state!=void', label: 'Pending' },
              ],
              type: uiGridConstants.filter.SELECT
            },
            exportFormatter: (_grid, _row, _col, state) => state || 'pending'
          },
          toColObj({
            displayName: 'Start Date',
            field: 'data.StartEpochTime',
            cellFilter: 'secondsToDate:"medium"',
            enableCellEdit: false,
            enableFiltering: false, // Water Reports page already has a date range, so filter is redundant
            type: 'date',
            exportFormatter: (_grid, _row, _col, startDate) => secondsToDate(startDate)
              .toLocaleString(),
          }), {
            name: 'timeDifference',
            field: 'data.timeDifference',
            displayName: 'Time Difference',
            cellTooltip: true,
            enableCellEdit: false,
            pinnedLeft: false,
            width: 105,
            cellTemplate: `<div
                class='ui-grid-cell-contents'
                style="text-align: center"
                data-toggle="tooltip"
                title="Time difference">{{row.entity.data.timeDifference}}</div>`,
          },
          toColObj({
            displayName: 'Destination Service Time',
            name: 'data.TotalTime',
            type: 'number',
            cellFilter: 'minutesToTime',
            exportFormatter: (_grid, _row, _col, totalTime) => minutesToTime(totalTime).toLocaleString()
          }),
          toColObj({
            displayName: 'Driver Name',
            name: 'data.TruckDriverName',
          }),
          toColObj({
            displayName: 'Quantity',
            name: 'data.Volume',
            width: 100,
            type: 'number',
            aggregationLabel: 'Sum: ',
            aggregationType: (_rows, col) => _.get(metadata, _.snakeCase(col.field))
          }),
          toColObj({
            displayName: 'Notes',
            name: 'data.WellLease.LeaseNotes',
            width: 100,
          }),
          toColObj({
            displayName: 'Service Ticket Number',
            name: 'data.ServiceTicketNo',
            type: 'number'
          }),
          toColObj({
            displayName: 'Service Company Name',
            name: 'data.HaulerCo.HaulerCoName',
          }),
          toColObj({
            displayName: 'Source Operator Name',
            name: 'data.WellLease.Operator.OperatorName',
          }),
          toColObj({
            displayName: 'Source Lease Number',
            name: 'data.WellLease.LeaseNumber',
            type: 'number'
          }),
          toColObj({
            displayName: 'Source Lease Name',
            name: 'data.WellLease.LeaseName',
          }),
          toColObj({
            displayName: 'Source Well Number',
            name: 'data.WellLease.WellNumber',
            // In reality, our users well numbers contain letters and spaces as well.
          }),
          toColObj({
            displayName: 'Material Name',
            name: 'data.MaterialName',
            width: 125,
            editableCellTemplate: 'ui-grid/dropdownEditor',
            editDropdownOptionsArray: [
              { value: 'Flowback' },
              { value: 'Other Water' },
              { value: 'Production' },
            ],
            editDropdownIdLabel: 'value',
            editDropdownValueLabel: 'value',
            visible: false
          }),
          toColObj({
            displayName: 'Source Operator Number',
            name: 'data.WellLease.Operator.OperatorNumber',
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Source Field Name',
            name: 'data.WellLease.FieldName',
            visible: false
          }),
          toColObj({
            displayName: 'Source Field Number',
            name: 'data.WellLease.FieldNumber',
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Truck Number',
            name: 'data.TruckNumber',
            width: 125,
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Manually Entered',
            name: 'data.IsWellFound',
            type: 'number',
            cellTemplate: `<div
                class='ui-grid-cell-contents'
                data-toggle="tooltip"
                title="{{grid.getCellValue(row, col) ? 'False' : 'True'}}"
                ng-class="{'text-danger bg-danger': !grid.getCellValue(row, col)}">
                  {{grid.getCellValue(row, col) ? 'False' : 'True'}}
                </div>`,
            editableCellTemplate: 'ui-grid/dropdownEditor',
            editDropdownOptionsArray: [
              { label: 'True', value: 0 },
              { label: 'False', value: 1 },
            ],
            // I think ui-grid has their syntax backwards for ng-options
            // ng-options="field[editDropdownIdLabel] as field[editDropdownValueLabel] for ..."
            editDropdownIdLabel: 'value',
            editDropdownValueLabel: 'label',
            filter: {
              selectOptions: [
                { value: '0', label: 'True' },
                { value: '1', label: 'False' },
              ],
              type: uiGridConstants.filter.SELECT
            },
            exportFormatter: (_grid, _row, _col, found) => ['false', 'true'][found],
            visible: false
          }),
          toColObj({
            displayName: 'Source Drilling Permit Number',
            name: 'data.WellLease.DrillingPermitNo',
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Source API Number',
            name: 'data.WellLease.LeaseAPINo',
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Disposal Ticket Number',
            name: 'data.DisposalTicketNo',
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Lane Number',
            name: 'data.SWDSiteLaneID',
            width: 125,
            type: 'number',
            visible: false
          }),
          toColObj({
            displayName: 'Destination Site Name',
            name: 'data.SWDSite.SWDSiteName',
            editableText: 'data.SWDSite.SWDSiteName',
            visible: false
          }),
          toColObj({
            displayName: 'Rate',
            name: 'data.Rate',
            width: 100,
            type: 'number',
            cellFilter: 'number: 2',
            exportFormatter: (_grid, _row, _col, rate) => _.isNumber(rate) ? rate.toFixed(2) : rate,
            visible: false
          }),
          toColObj({
            displayName: 'Total Charge',
            name: 'data.TotalCharge',
            width: 125,
            visible: false
          }),
          toColObj({
            displayName: 'End Date',
            name: 'data.StopEpochTime',
            cellFilter: 'secondsToDate:"medium"',
            enableCellEdit: false,
            enableFiltering: false, // Water Reports page already has a date range, so filter is redundant
            type: 'date',
            exportFormatter: (_grid, _row, _col, endDate) => secondsToDate(endDate).toLocaleString(),
            visible: false
          }),
          toColObj({
            displayName: 'Description',
            name: 'data.TransactionNotes',
            width: 125,
            visible: false
          }),
          toColObj({
            displayName: 'Event ID',
            name: 'id',
            enableCellEdit: false,
            visible: false
          }),
        ],
      },
      generic: {
        title: 'Events',
        columnDefs: [
          {
            name: 'flag',
            title: 'Flag',
            dataType: 'boolean',
            editor: 'checkbox',
            mask: '',
            path: 'flag'
          },
          {
            name: 'flag',
            title: 'Flag',
            dataType: 'boolean',
            editor: 'checkbox',
            mask: '',
            path: 'flag'
          },
          {
            name: 'flag',
            title: 'Flag',
            dataType: 'boolean',
            editor: 'checkbox',
            mask: '',
            path: 'flag'
          }
        ]
      }
    }[reportType];
  }

  function waterDisposalExportFieldCallback(grid, row, col, value) {
    return col.colDef.exportFormatter?.(grid, row, col, value) ?? value;
  }

  /**
   * Evaluates each event's start-time with the previous and adds new attr `timeDifference` with result
   * (Useful to find out if there are possible duplicate rows)
   *
   * @param {object[]} events
   */
  function calculateTimeDifference(events) {
    let previous;
    _.forEach(events, (element) => {
      if (_.isFinite(previous?.data?.StartEpochTime) && _.isFinite(element?.data?.StartEpochTime)) {
        element.data.timeDifference = formatDistanceStrict(
          secondsToDate(previous.data.StartEpochTime),
          secondsToDate(element.data.StartEpochTime));
      }
      previous = element;
    });
  }

  function generateReportName() {
    const [start, end] = [vm.options.date.start, vm.options.date.end].map(date => $filter('date')(date, 'yyyyMMdd'));
    const timestamp = start === end ? start : `${start}_${end}`;
    vm.gridOptions.exporterCsvFilename = `${_.snakeCase(vm.selectedSite?.name)}_Trucks_${timestamp}.csv`;
  }
}
